/**
 * Kafelki z GET /ksef/otrzymane/podsumowanie: liczby per status (klik = filtr statusu tabeli; "Wszystkie" = razem,
 * z dopiskiem "XML do pobrania" gdy sa) + "Per miesiąc" (miesiace[{miesiac,liczba,brutto,nowe}]) + "Sprzedawcy"
 * (sprzedawcy[{nip,nazwa,liczba,brutto,nowe,sfd}], klik = filtr NIP).
 * Zaznaczony kafelek = obrys w kolorze statusu (nie sama ramka - dodatkowo pelne tlo kafelka).
 */
import React from 'react';
import { formatujKwote, formatujMiesiac, KAFELKI_STATUSOW } from './format';

const Kafelek = ({ id, etykieta, wartosc, dopisek, kolor, tlo, aktywny, onKlik }) => (
  <div
    data-testid={`kafelek-${id}`}
    role="button"
    tabIndex={0}
    aria-pressed={aktywny}
    onClick={onKlik}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onKlik(); } }}
    title="Kliknij, żeby przefiltrować tabelę"
    style={{
      flex: '1 1 120px', minWidth: '120px', padding: '8px 10px', borderRadius: '6px',
      backgroundColor: tlo, borderLeft: `3px solid ${kolor}`,
      outline: aktywny ? `2px solid ${kolor}` : 'none',
      cursor: 'pointer',
    }}
  >
    <div style={{ fontSize: '18px', fontWeight: 700, color: kolor, lineHeight: 1.2 }}>{wartosc}</div>
    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>{etykieta}</div>
    {dopisek && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{dopisek}</div>}
  </div>
);

/** Props: statusy (podsumowanie.statusy), razem, xmlDoPobrania, aktywnyStatus (null = wszystkie), onStatus(status|null). */
const PodsumowanieKsef = ({ statusy, razem, xmlDoPobrania, aktywnyStatus, onStatus }) => {
  const l = { ...(statusy || {}), razem };
  const n = (k) => (l[k] === null || l[k] === undefined ? '—' : l[k]);
  return (
    <div data-testid="podsumowanie-ksef" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '12px' }}>
      {KAFELKI_STATUSOW.map(k => (
        <Kafelek
          key={k.id} id={k.id} etykieta={k.etykieta} wartosc={n(k.licznik)}
          dopisek={k.id === 'wszystkie' && Number(xmlDoPobrania) > 0 ? `XML do pobrania: ${xmlDoPobrania}` : undefined}
          kolor={k.kolor} tlo={k.tlo}
          aktywny={(aktywnyStatus || null) === k.filtr}
          onKlik={() => onStatus && onStatus(k.filtr)}
        />
      ))}
    </div>
  );
};

const th = { padding: '5px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const td = { padding: '4px 8px', borderBottom: '1px solid var(--border)', fontSize: '11px' };
const prawa = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };

/** Tabela per miesiac (wg daty wystawienia): [{miesiac: 'RRRR-MM', liczba, brutto, nowe}]. */
export const PerMiesiacKsef = ({ miesiace }) => {
  const lista = Array.isArray(miesiace) ? miesiace : [];
  return (
    <div className="card" data-testid="per-miesiac-ksef" style={{ fontSize: '12px' }}>
      <h3 style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600 }}>Per miesiąc (wg daty wystawienia)</h3>
      {lista.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Brak danych — faktury pojawią się po pobraniu z KSeF</div>
      ) : (
        <div style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Miesiąc</th>
                <th style={{ ...th, textAlign: 'right' }}>Faktur</th>
                <th style={{ ...th, textAlign: 'right' }}>Nowych</th>
                <th style={{ ...th, textAlign: 'right' }}>Brutto</th>
              </tr>
            </thead>
            <tbody>
              {lista.map(m => (
                <tr key={m.miesiac} data-testid="per-miesiac-wiersz">
                  <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>{formatujMiesiac(m.miesiac)}</td>
                  <td style={prawa}>{m.liczba ?? '—'}</td>
                  <td style={{ ...prawa, color: Number(m.nowe) > 0 ? 'var(--warning)' : 'var(--text-muted)', fontWeight: Number(m.nowe) > 0 ? 600 : 400 }}>{m.nowe ?? '—'}</td>
                  <td style={{ ...prawa, fontWeight: 600 }}>{formatujKwote(m.brutto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/** Tabela sprzedawcow: [{nip, nazwa, liczba, brutto, nowe, sfd}]. Klik -> onWybierz(nip, sprzedawca); klik w aktywnego -> ''. */
export const TopSprzedawcy = ({ sprzedawcy, aktywnyNip, onWybierz }) => {
  const lista = Array.isArray(sprzedawcy) ? sprzedawcy : [];
  return (
    <div className="card" data-testid="top-sprzedawcy" style={{ fontSize: '12px' }}>
      <h3 style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600 }}>Sprzedawcy (najwięcej faktur)</h3>
      {lista.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Brak danych — faktury pojawią się po pobraniu z KSeF</div>
      ) : (
        <div style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Sprzedawca</th>
                <th style={th}>NIP</th>
                <th style={{ ...th, textAlign: 'right' }}>Faktur</th>
                <th style={{ ...th, textAlign: 'right' }}>Nowych</th>
                <th style={{ ...th, textAlign: 'right' }}>Brutto</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((s, idx) => {
                const aktywny = Boolean(aktywnyNip) && String(aktywnyNip) === String(s.nip);
                return (
                  <tr key={`${s.nip || idx}`} data-testid="sprzedawca-wiersz" role="button" tabIndex={0}
                    title="Kliknij, żeby pokazać faktury tego sprzedawcy"
                    onClick={() => onWybierz && onWybierz(aktywny ? '' : s.nip, s)}
                    onKeyDown={(e) => { if (onWybierz && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onWybierz(aktywny ? '' : s.nip, s); } }}
                    style={{ cursor: 'pointer', backgroundColor: aktywny ? 'var(--primary-light)' : 'transparent' }}>
                    <td style={{ ...td, fontWeight: 600 }}>
                      {s.nazwa || '—'}
                      {s.sfd && <span data-testid="sprzedawca-sfd" title="Dostawca towarowy SFD — faktury trafiają też do Kontroli faktur SFD" style={{ marginLeft: '6px', fontSize: '10px', padding: '1px 6px', borderRadius: '8px', color: 'var(--info)', backgroundColor: 'var(--info-light)', fontWeight: 600 }}>SFD</span>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{s.nip || '—'}</td>
                    <td style={prawa}>{s.liczba ?? '—'}</td>
                    <td style={{ ...prawa, color: Number(s.nowe) > 0 ? 'var(--warning)' : 'var(--text-muted)', fontWeight: Number(s.nowe) > 0 ? 600 : 400 }}>{s.nowe ?? '—'}</td>
                    <td style={{ ...prawa, fontWeight: 600 }}>{formatujKwote(s.brutto)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PodsumowanieKsef;
